import { useState, useEffect } from 'react';
import { formatDate as formatDateSafe } from '../utils/formatDate';
import { X, FileText, Calendar, DollarSign, CheckCircle2, Clock, Circle, History, Share2, UserCheck } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

import { ChangeOrderBuilder, ChangeOrderDraft } from './ChangeOrderBuilder';
import { ChangeOrderLifecyclePanel } from './ChangeOrderLifecyclePanel';
import { formatDollars } from '../utils/fixedBidMarkupText';
import { storedFixedBidQuote } from '../utils/fixedBidMarkup';
import { ScopeDelta } from './ScopeDeltaReview';
import { SOWTeamAndIntegrations } from './SOWTeamAndIntegrations';

import { toast } from 'sonner';

interface SOW {
  id: string;
  companyId: string;
  companyName: string;
  consultingFirmId: string;
  name: string;
  description: string;
  status: 'draft' | 'pending_approval' | 'approved' | 'active' | 'completed' | 'rejected';
  deliverables: Array<{
    id: string;
    name: string;
    description: string;
    dueDate: string;
    status: 'pending' | 'in_progress' | 'completed';
  }>;
  pricing: {
    type: 'fixed' | 'hourly' | 'hybrid';
    amount: number;
    hourlyRate?: number;
    estimatedHours?: number;
    /** A fixed bid's markup: `amount` is the bid total, `basePrice` the bid before markup. */
    basePrice?: number;
    markupPercent?: number;
    markupAmount?: number;
  };
  timeline: {
    startDate: string;
    endDate: string;
    milestones: Array<{
      id: string;
      name: string;
      date: string;
      status: 'pending' | 'completed';
    }>;
  };
  createdAt: string;
  createdBy: string;
  ownerId?: string;
  ownerName?: string;
  engagementLeadId?: string;
  engagementLeadName?: string;
  /** Names typed on the SOW and the firm users assigned to it (the Team section). */
  teamMembers?: string[];
  assignedConsultants?: string[];
  approvedAt?: string;
  approvedBy?: string;
  changeOrders?: Array<{
    id: string;
    changeOrderId: string;
    description: string;
    status: string;
    dateAdded: string;
    scopeChanges: string;
  }>;
}

interface SOWDetailsModalProps {
  sowId: string | null;
  isOpen: boolean;
  onClose: () => void;
  accessToken: string;
  projectId: string;
  onViewDetails?: (sowId: string) => void;
  onEdit?: (sowId: string) => void;
  onViewHistory?: (sowId: string) => void;
  onShare?: (sowId: string) => void;
  /** The signed-in user (the Slack/Teams test message names them). */
  currentUser?: any;
}

export function SOWDetailsModal({ sowId, isOpen, onClose, accessToken, projectId, onViewDetails, onEdit, onViewHistory, onShare, currentUser }: SOWDetailsModalProps) {
  const [sow, setSOW] = useState<SOW | null>(null);
  // The engagement lead: the firm user accountable for the engagement; changeable at any status.
  const [firmUsers, setFirmUsers] = useState<Array<{ id: string; name?: string; email?: string }>>([]);
  const [changingLead, setChangingLead] = useState(false);
  const loadFirmUsers = async (firmId?: string) => {
    if (!firmId) return;
    try {
      const r = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${encodeURIComponent(firmId)}/users`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
      if (r.ok) { const b = await r.json(); setFirmUsers(Array.isArray(b.users) ? b.users : []); }
    } catch { /* the lead still shows by name */ }
  };
  const changeLead = async (id: string) => {
    if (!sow || !id) return;
    const u = firmUsers.find((x) => x.id === id);
    const name = u ? (u.name || u.email || id) : id;
    setChangingLead(true);
    try {
      const r = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${sow.id}`, {
        method: 'PATCH', headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ engagementLeadId: id, engagementLeadName: name }),
      });
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `Request failed (${r.status})`); }
      setSOW((prev) => (prev ? { ...prev, engagementLeadId: id, engagementLeadName: name } : prev));
      toast.success(`${name} leads this engagement now`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not change the engagement lead');
    } finally { setChangingLead(false); }
  };
  const [loading, setLoading] = useState(false);

  const [draftingCO, setDraftingCO] = useState(false);
  const [coDeltas, setCoDeltas] = useState<ScopeDelta[]>([]);

  useEffect(() => {
    if (isOpen && sowId) {
      fetchSOW();
    }
  }, [isOpen, sowId]);

  const fetchSOW = async () => {
    if (!sowId) return;
    
    setLoading(true);
    try {
      console.log('[SOWDetailsModal] Fetching SOW:', sowId);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${sowId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[SOWDetailsModal] Received SOW data:', data);
        setSOW(data.sow);
        loadFirmUsers(data.sow?.consultingFirmId);
      } else {
        console.error('[SOWDetailsModal] Failed to fetch SOW:', response.status, await response.text());
      }
    } catch (error) {
      console.error('[SOWDetailsModal] Error fetching SOW:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: SOW['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
    }
  };

  const getDeliverableIcon = (status: 'pending' | 'in_progress' | 'completed') => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="size-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="size-5 text-blue-600" />;
      case 'pending':
        return <Circle className="size-5 text-gray-400" />;
    }
  };

  // '' for a missing or unreadable date, never "Invalid Date" (audit finding C5).
  const formatDate = (dateString: string | null | undefined) =>
    formatDateSafe(dateString, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US') || 'Not set';

  const handleDraftChangeOrder = async () => {
     if (!sow) return;
     
     const toastId = toast.loading("Finding items for change order...");
     try {
         // Fetch config items for this company to check deltas
         // We might need an endpoint that returns all deltas for a company/SOW
         // Or we fetch CIs and then fetch artifacts.
         // Let's assume there's an endpoint or we iterate. Iterating CIs for now.
         
         const resCIs = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${sow.companyId}/config-items`, {
             headers: { 'Authorization': `Bearer ${accessToken}` }
         });
         
         if (!resCIs.ok) throw new Error("Failed to fetch config items");
         const { data: configItems } = await resCIs.json();
         
         const allCoDeltas: ScopeDelta[] = [];
         
         // In parallel fetch deltas & decisions
         await Promise.all(configItems.map(async (item: any) => {
             // We need artifacts. 
             // We can check if artifacts are returned in list or fetch individually.
             // Usually artifacts are large so might be separate.
             // We can use the endpoint /config-items/{id}/artifacts/scopeDelta
             // And /config-items/{id}/artifacts/scopeDeltaDecisions
             
             try {
                 const [resDelta, resDecisions] = await Promise.all([
                     fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${sow.companyId}/${item.id}/artifacts/scopeDelta`, { headers: { 'Authorization': `Bearer ${accessToken}` } }),
                     fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${sow.companyId}/${item.id}/artifacts/scopeDeltaDecisions`, { headers: { 'Authorization': `Bearer ${accessToken}` } })
                 ]);
                 
                 if (resDelta.ok && resDecisions.ok) {
                     const deltas = await resDelta.json();
                     const decisions = await resDecisions.json();
                     
                     // Filter for prepare_change_order
                     if (deltas && Array.isArray(deltas)) {
                        deltas.forEach((d: any) => {
                             // decisions is object keyed by deltaId { decision: '...', ... }
                             // OR it might be an array. Let's assume object as per ScopeDeltaReview.
                             const decision = decisions ? decisions[d.deltaId]?.decision : null;
                             if (decision === 'prepare_change_order') {
                                 allCoDeltas.push(d);
                             }
                        });
                     }
                 }
             } catch (err) {
                 console.warn("Failed to check item", item.id, err);
             }
         }));
         
         if (allCoDeltas.length === 0) {
             toast.info("No items marked for change order found.", { id: toastId });
             return;
         }
         
         setCoDeltas(allCoDeltas);
         setDraftingCO(true);
         toast.dismiss(toastId);
         
     } catch (e) {
         console.error(e);
         toast.error("Failed to prepare change order draft", { id: toastId });
     }
  };

  const handleActivateSOW = async () => {
    if (!sow) return;
    if (!confirm("Are you sure you want to activate this SOW? This will finalize the scope baseline.")) return;

    try {
        const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/sows/${sow.id}/status`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status: 'active' })
        });
        
        if (response.ok) {
            toast.success("SOW activated and baseline generated");
            fetchSOW(); // Refresh
        } else {
            throw new Error("Failed to activate");
        }
    } catch (e) {
        console.error(e);
        toast.error("Failed to activate SOW");
    }
  };

  const calculateProgress = () => {
    if (!sow || !sow.deliverables || sow.deliverables.length === 0) return 0;
    const completed = sow.deliverables.filter(d => d.status === 'completed').length;
    return (completed / sow.deliverables.length) * 100;
  };

  if (!isOpen) return null;

  if (draftingCO && sow) {
      return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
             <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] p-6 overflow-hidden flex flex-col">
                 <ChangeOrderBuilder
                    selectedDeltas={coDeltas}
                    companyId={sow.companyId}
                    configItemId={sow.id} // associating CO with SOW ID might be tricky if builder expects configItemId.
                    // The backend route for CO builder is /config-items/{companyId}/{configItemId}/change-orders
                    // We might need to adjust the builder to handle SOW-level COs or pick a "main" config item.
                    // Or we pass a dummy ID and handle it on backend?
                    // The prompt says: "Aggregates deltas... Opens ChangeOrderBuilder".
                    // The builder props: configItemId.
                    // Let's pass the SOW ID as configItemId and hope backend handles it or we update builder.
                    // Actually, ChangeOrderBuilder saves to /config-items/...
                    // We should probably modify ChangeOrderBuilder to support SOW level?
                    // For now let's pass SOW ID and ensure backend route exists or use a workaround.
                    // If we use SOW ID, the URL becomes .../config-items/{sowId}/change-orders. 
                    // If the backend treats {configItemId} just as a container ID (bucket), it might work.
                    // Let's proceed with SOW ID.
                    userId={accessToken} // Wait, userId should be extracted from token or passed. 
                    // SOWDetailsModal doesn't have currentUser object, just accessToken.
                    // We might need to decode token or pass user.
                    // SOWDetailsModalProps has accessToken.
                    // Let's assume we can get user ID or pass a placeholder if builder handles it.
                    // Actually ChangeOrderBuilder takes userId and userName.
                    // We don't have them in props here. We might need to add them to props.
                    userName={"Consultant"} // Placeholder
                    onBack={() => setDraftingCO(false)}
                    onSave={() => {
                        setDraftingCO(false);
                        fetchSOW();
                    }}
                 />
             </div>
        </div>
      );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <FileText className="size-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold">Statement of Work</h2>
              {sow && (
                <p className="text-sm text-muted-foreground">{sow.companyName}</p>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : sow ? (
            <div className="space-y-6">
              {/* Overview */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 mr-4">
                    <h3 className="text-xl font-semibold mb-2">{sow.name}</h3>
                    <div className="text-muted-foreground space-y-2 whitespace-pre-wrap leading-relaxed">
                      {sow.description}
                    </div>
                  </div>
                  <Badge className={getStatusColor(sow.status)}>
                    {sow.status.replace('_', ' ')}
                  </Badge>
                </div>

                {/* Progress */}
                {sow.deliverables && sow.deliverables.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Overall Progress</span>
                      <span className="text-sm text-muted-foreground">
                        {sow.deliverables.filter(d => d.status === 'completed').length} / {sow.deliverables.length} deliverables
                      </span>
                    </div>
                    <Progress value={calculateProgress()} className="h-2" />
                  </div>
                )}
              </div>

              {/* Key Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4" data-testid="sow-lead-card">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="size-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Engagement lead</span>
                  </div>
                  <p className="text-lg font-semibold truncate">{sow.engagementLeadName || sow.ownerName || 'Not set'}</p>
                  {firmUsers.length > 0 && (
                    <Select value={sow.engagementLeadId || sow.ownerId || ''} onValueChange={changeLead} disabled={changingLead}>
                      <SelectTrigger className="mt-2 h-8 text-xs" aria-label="Change engagement lead"><SelectValue placeholder="Change" /></SelectTrigger>
                      <SelectContent>
                        {firmUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email || u.id}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="size-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Pricing</span>
                  </div>
                  <p className="text-2xl font-bold">
                    {sow.pricing?.amount ? `$${sow.pricing.amount.toLocaleString()}` : 'TBD'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {sow.pricing?.type === 'fixed' && (() => {
                      const quote = storedFixedBidQuote(sow.pricing);
                      return quote ? `Fixed price: ${formatDollars(quote.base)} + ${quote.percent}% markup (${formatDollars(quote.markup)})` : 'Fixed price';
                    })()}
                    {sow.pricing?.type === 'hourly' && `$${sow.pricing.hourlyRate}/hr`}
                    {sow.pricing?.type === 'hybrid' && `Hybrid: $${sow.pricing.hourlyRate}/hr`}
                    {!sow.pricing?.type && 'Pricing not set'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="size-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Start Date</span>
                  </div>
                  <p className="text-lg font-semibold">
                    {sow.timeline?.startDate ? formatDate(sow.timeline.startDate) : 'Not set'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="size-4 text-muted-foreground" />
                    <span className="text-sm font-medium">End Date</span>
                  </div>
                  <p className="text-lg font-semibold">
                    {sow.timeline?.endDate ? formatDate(sow.timeline.endDate) : 'Not set'}
                  </p>
                </div>
              </div>

              {/* Team, config items and the Slack/Teams settings (the retired SOWDetailView's tabs, RESTORE_PLAN R3.5) */}
              <SOWTeamAndIntegrations sow={sow} firmUsers={firmUsers} accessToken={accessToken} projectId={projectId} currentUser={currentUser} />

              {/* Deliverables */}
              <div>
                <h4 className="text-base font-semibold mb-3">Deliverables</h4>
                {sow.deliverables && sow.deliverables.length > 0 ? (
                  <div className="space-y-2">
                    {sow.deliverables.map((deliverable) => (
                      <div
                        key={deliverable.id}
                        className="flex items-start gap-3 p-3 border rounded-lg"
                      >
                        {getDeliverableIcon(deliverable.status)}
                        <div className="flex-1">
                          <h5 className="font-medium mb-1">{deliverable.name}</h5>
                          <p className="text-sm text-muted-foreground mb-2">
                            {deliverable.description}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Due: {formatDate(deliverable.dueDate)}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {deliverable.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                    No deliverables defined
                  </p>
                )}
              </div>

              {/* Milestones */}
              <div>
                <h4 className="text-base font-semibold mb-3">Milestones</h4>
                {sow.timeline?.milestones && sow.timeline.milestones.length > 0 ? (
                  <div className="space-y-2">
                    {sow.timeline.milestones.map((milestone, idx) => (
                      <div
                        key={milestone.id}
                        className="flex items-center gap-3 p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          {milestone.status === 'completed' ? (
                            <CheckCircle2 className="size-5 text-green-600" />
                          ) : (
                            <Circle className="size-5 text-gray-400" />
                          )}
                          <span className="text-sm font-medium">{milestone.name}</span>
                        </div>
                        <div className="flex-1"></div>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(milestone.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg">
                    No milestones defined
                  </p>
                )}
              </div>

              {/* Change Orders — lifecycle per PRD §4.4.1 (draft → pending approval → approved → applied → billed) */}
              <ChangeOrderLifecyclePanel
                engagementId={sow.id}
                accessToken={accessToken}
                extraActions={
                  <Button size="sm" variant="outline" onClick={handleDraftChangeOrder}>
                    Draft document from scope deltas
                  </Button>
                }
                onChanged={() => { if (sowId) fetchSOW(); }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              SOW not found
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6 flex justify-between">
          <div className="flex gap-2">
            {/* Edit SOW removed: executed SOWs are immutable. The
                onEdit callback is intentionally ignored even when
                callers still pass it, so removing this button is
                sufficient to retire the feature without breaking
                existing prop contracts. */}
            {onViewHistory && sow && (
              <Button variant="outline" onClick={() => onViewHistory(sow.id)}>
                <History className="size-4 mr-2" />
                Version History
              </Button>
            )}
            {onShare && sow && (
              <Button variant="outline" onClick={() => onShare(sow.id)}>
                <Share2 className="size-4 mr-2" />
                Share
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {sow && (sow.status === 'pending_approval' || sow.status === 'approved') && (
               <Button onClick={handleActivateSOW} className="bg-green-600 hover:bg-green-700">
                   Activate & Baseline
               </Button>
            )}
            {sow && sow.status === 'active' && (
              <Button>Update Progress</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}